// src/app/modules/user/components/profile-detail/BloodPressureAlerts.tsx

import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap' 
import BloodPressureIcon from '../common/BloodPressureIcon'
import {useReduxAuth} from '../../../auth'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {AlertStatus} from '../doctor-dashboard/BloodPressureAlerts'
import BPAlertCard, {Reading} from './BPAlertCard'
import {FiInbox} from 'react-icons/fi'
import {getRandomMotivationalMessage} from '../../../../constants/motivationalMessages'
import {toast} from 'react-toastify'

export interface BPCategory {
  category: AlertStatus
  count: number
  readings: Reading[]
}

export interface BPCategoriesApiResponse {
  status: string
  message: string
  data: {categories: BPCategory[]}
}

interface BloodPressureAlertsProps {
  patientId: string
}

// TODO:: Rename to
const BloodPressureAlerts: React.FC<BloodPressureAlertsProps> = ({patientId}) => {
  const {currentUser} = useReduxAuth()

  const [_summaryBadges, setSummaryBadges] = useState<Record<AlertStatus, number>>({
    'Crisis Alerts': 0,
    'Stage 2 Alert': 0,
    'Stage 1 Alert': 0,
    Elevated: 0,
    Low: 0,
  })

  const [alerts, setAlerts] = useState<(Reading & {status: AlertStatus})[]>([])
  const [showModal, setShowModal] = useState(false)
  const [motivationalMessage, setMotivationalMessage] = useState('')

  const handleShowModal = () => setShowModal(true)
  const handleCloseModal = () => setShowModal(false)

  useEffect(() => {
    const fetchAlerts = async () => {
      if (!currentUser?.id) return

      try {
        const {data: resp} = await axios.get<BPCategoriesApiResponse>(
          API_ENDPOINTS.GET_BLOOD_PRESSURE_ALERTS(patientId)
        )

        // filter out any "Low" category entirely
        const filteredCats = resp.data.categories.filter((cat) => cat.category !== 'Low')

        // rebuild summary counts
        const summary: Record<AlertStatus, number> = {
          'Crisis Alerts': 0,
          'Stage 2 Alert': 0,
          'Stage 1 Alert': 0,
          Elevated: 0,
          Low: 0,
        }

        // flatten readings with their status
        const allAlerts: (Reading & {status: AlertStatus})[] = []
        filteredCats.forEach(({category, count, readings}) => {
          summary[category] = count
          readings.forEach((r) => allAlerts.push({...r, status: category}))
        })

        // sort newest first
        allAlerts.sort(
          (a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime()
        )

        setSummaryBadges(summary)
        setAlerts(allAlerts)

        // Set motivational message if no alerts
        if (allAlerts.length === 0) {
          // TODO::Currently it will show even for new patient. If we can pass flag if this is new patient with no readings, we can both avoid API call for alerts as well as show 'generic' message
          setMotivationalMessage(getRandomMotivationalMessage('individual-patient'))
        }
      } catch (err) {
        console.error('Failed to fetch Health Alerts', err)
      }
    }

    fetchAlerts()
  }, [currentUser, patientId])
  const handleResolveAlert = async (readingId: number) => {
    try {
      await axios.put(
        API_ENDPOINTS.UPDATE_READING_ALERT(patientId, readingId),
        {},
        {params: {reading_id: readingId}}
      )
      // Remove the resolved alert from local state
      setAlerts((prevAlerts) => prevAlerts.filter((a) => a.readingId !== readingId))
      toast.success('Successfully resolved the reading.')
    } catch {
      toast.error('Failed to resolve. Please try again.')
    }
  }

  const displayedAlerts = alerts.slice(0, 2)
  const hasAlerts = alerts.length > 0

  return (
    <>
      <div className='bg-white p-4 rounded-4 border'>
        {/* header row */}
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <h3>Health Alerts</h3>

          {alerts.length > 2 && (
            <span
              role='button'
              onClick={handleShowModal}
              className='fw-semibold text-base-gray d-flex align-items-center gap-2'
            >
              View All
            </span>
          )}
        </div>

        {!hasAlerts ? (
          <div className='custom-card-outlined_1 p-3'>
            <div className='d-flex flex-column align-items-center justify-content-center text-center text-muted py-3'>
              <FiInbox size={32} />
              <p className='mb-0 mt-2 fs-5'>{motivationalMessage}</p>
            </div>
          </div>
        ) : (
          <div className='row row-cols-1 row-cols-lg-2 g-3'>
            {displayedAlerts.map((alert, i) => (
              <div className='col' key={i}>
                <BPAlertCard alert={alert} patientUUID={patientId} onResolve={handleResolveAlert} />
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        size='xl'
        contentClassName='border-0 shadow-lg'
      >
        <Modal.Header closeButton className='border-bottom-0 pb-0'>
          <Modal.Title className='fw-bold text-dark'>
            All Blood Pressure Alerts
            <div className='text-muted fs-6'>{alerts.length} total alerts recorded</div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className='modal-style'>
          {!alerts.length ? (
            <div className='text-center py-5'>
              <BloodPressureIcon width={48} height={48} fillColor='#333333' />
              <h3 className='h5 text-muted'>No blood pressure alerts found</h3>
            </div>
          ) : (
            <div className='row row-cols-1 row-cols-md-1 row-cols-lg-2 g-4'>
              {alerts.map((alert, i) => (
                <div className='col' key={i}>
                  <BPAlertCard
                    alert={alert}
                    patientUUID={patientId}
                    onResolve={handleResolveAlert}
                  />
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default BloodPressureAlerts
